import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { htmlHasTextOrImg } from '@frontend-site/shared/utils';
import { PExpandAreaTriggerComponent } from '@panaya/ui/p-expand-area-trigger';
import suneditor from 'suneditor';
import SunEditor from 'suneditor/src/lib/core';
import plugins from 'suneditor/src/plugins';
import { PFieldAbstractComponent } from '../p-field-abstract';

export interface RichTextScrollEvent {
    textAreaElement: HTMLTextAreaElement;
    event: Event;
    scrollTop?: number;
}

@Component({
    selector: 'p-field-rich-text',

    template: `
        <span>
            <p-field
                [ngClass]="parentCustomClasses"
                [isMandatory]="isMandatory"
                [label]="label"
                [labelTooltip]="labelTooltip"
                [isReadOnly]="isReadOnly"
                [customClasses]="customClasses"
                [shouldTransformLabelTextCase]="shouldTransformLabelTextCase"
            >
                <div class="textarea-div" slot="readMode" [hidden]="!isReadOnly">
                    <textarea #sunEditorElemRead></textarea>
                </div>
                <div class="textarea-div" slot="editMode" [hidden]="isReadOnly">
                    <textarea #sunEditorElemEdit></textarea>
                </div>
            </p-field>
        </span>
        <p-expand-area-trigger
            #expandableArea
            *ngIf="showMoreEnabled && innerWrapper"
            [addReadMoreSpan]="!!label"
            [element]="innerWrapper"
            (expandElement)="expandElement($event)"
        ></p-expand-area-trigger>
    `,
    styleUrls: ['./p-field-rich-text.component.scss'],

    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldRichTextComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldRichText'
})
export class PFieldRichTextComponent extends PFieldAbstractComponent<string> implements AfterViewInit {
    @Input() label?: string;
    @Input() isMandatory = false;
    @Input() isMultiline = false;
    @Input() textCustomClass?: string;
    @Input() isInEditMode = true;
    @Input() showMoreEnabled = true;
    @Input() customClasses?: string;
    @Input() parentCustomClasses: string = '';
    @Input() override isDisabled = false;
    // May implement this or not according to need;
    @Input() maxlength?: number;
    @Input() placeholder?: string;
    @Input() formatValueOnInit?: (value: any) => any;
    @Output() richTextScroll = new EventEmitter<RichTextScrollEvent>();

    @ViewChild('sunEditorElemRead') sunEditorElemRead = {} as HTMLTextAreaElement;
    @ViewChild('sunEditorElemEdit') sunEditorElemEdit = {} as HTMLTextAreaElement;
    @ViewChild('expandableArea') expandAreaTriggerComponent?: PExpandAreaTriggerComponent;
    sunEditorElem: any;
    protected innerWrapper?: HTMLDivElement;

    sunEditor?: SunEditor;

    constructor(cd: ChangeDetectorRef, private elRef: ElementRef) {
        super(cd);
    }

    setScrollPosition(val: number): void {
        //the scroll is at inner element of the sunEditor, so couldn't use ViewChild and this 3rd party library has no function for this
        const div = this.elRef.nativeElement.querySelector('.sun-editor-editable');
        div.scrollTop = val;
    }

    changeValue(data: string) {
        this.markAsTouched();

        if (!this.isDisabled) {
            this.value = htmlHasTextOrImg(data) ? data : '';
            this._onChange(this.value);

            this.expandAreaTriggerComponent?.triggerCheckIsAreaExpandable();
        }
    }

    ngAfterViewInit(): void {
        if (this.isReadOnly) {
            this.createReadOnlyEditor();
        } else {
            this.createEditableEditor();
        }
    }

    private createEditableEditor() {
        this.sunEditorElem = this.sunEditorElemEdit;
        this.sunEditor = suneditor.create(this.sunEditorElem.nativeElement, {
            linkProtocol: 'http://',
            mediaAutoSelect: false,
            plugins,
            value: this.formattedValue(),
            font: ['Inter', 'Open Sans', 'sans-serif'],
            buttonList: [
                ['undo', 'redo'],
                ['fontSize', 'formatBlock'],
                ['bold', 'underline', 'italic', 'strike', 'superscript'],
                ['fontColor', 'hiliteColor'],
                ['removeFormat'],
                ['outdent', 'indent'],
                ['align', 'list'],
                ['table', 'link'],
                ['fullScreen']
            ]
        });
        this.sunEditor.toolbar.hide();
        this.sunEditor.onBlur = () => {
            this.sunEditor?.toolbar.hide();
        };
        this.sunEditor.onFocus = () => {
            this.sunEditor?.toolbar.show();
        };
        this.sunEditor.onChange = (value: any) => {
            this.changeValue(value);
        };
        this.sunEditor.toggleFullScreen = (isFullScreen: boolean) => {
            this.onFullScreen(isFullScreen);
        };
        this.sunEditor.onResizeEditor = (height, prevHeight, core) => {
            this.expandAreaTriggerComponent?.triggerCheckIsAreaExpandable();
            return {};
        };
        this.sunEditor.onScroll = (e: Event) => this.emitScrollEvent(e, this.sunEditorElemEdit);
        this.innerWrapper = this.getInnerWrapper();
        // Need to explicitly detect changes because Angular is not aware that we updated the innerWrapper
        this.cd.detectChanges();
    }

    private emitScrollEvent(event: Event, textAreaElement: HTMLTextAreaElement): void {
        if (!event.target)
            console.warn(
                'SunEditor emitted a scroll event which does not contain the event target. This might be a problem with the configuration or initialization of the editor.'
            );
        this.richTextScroll.emit({
            textAreaElement,
            event,
            scrollTop: (event.target as HTMLElement)?.['scrollTop']
        });
    }

    formattedValue(): string {
        this.value = this.value ? this.removeFontFamilyApostrophes(this.value) : this.value;
        return this.formatValueOnInit ? this.formatValueOnInit(this.value) : this.value;
    }

    private removeFontFamilyApostrophes(value: string): string {
        const regex = /font-family:([\s\w-,']*)[;]/gi;
        return value.replace(regex, (res: string) => res.replace(/'/g, ''));
    }

    private createReadOnlyEditor() {
        this.sunEditorElem = this.sunEditorElemRead;
        this.sunEditor = suneditor.create(this.sunEditorElem.nativeElement, { value: this.value });
        this.sunEditor.readOnly(true);
        this.sunEditor.toolbar.hide();
        this.sunEditor.disable();
        this.sunEditor.onScroll = (e: Event) => this.emitScrollEvent(e, this.sunEditorElemRead);
    }

    override writeValue(obj: string): void {
        super.writeValue(obj);
        if (this.sunEditor) {
            this.sunEditor.setContents(obj);
        }
    }

    public expandElement(expand: boolean): void {
        const element = this.getInnerWrapper();
        if (expand) {
            element.classList.replace('se-wrapper-inner', 'se-wrapper-inner-expanded');
            const needsImagePadding = this.getNeedsImagePadding();
            if (needsImagePadding) {
                element.classList.add('se-wrapper-inner-expanded-image-padding');
            }
        } else {
            element.classList.replace('se-wrapper-inner-expanded', 'se-wrapper-inner');
            element.classList.remove('se-wrapper-inner-expanded-image-padding');
        }
    }

    private getInnerWrapper(): HTMLDivElement {
        const element = this.sunEditorElem.nativeElement.parentElement
            ?.querySelector('div.sun-editor')
            ?.querySelector('div.se-container')
            ?.querySelector('div.se-wrapper');

        return element.querySelector('div.se-wrapper-inner.se-wrapper-wysiwyg.sun-editor-editable')
            ? element.querySelector('div.se-wrapper-inner.se-wrapper-wysiwyg.sun-editor-editable')
            : element.querySelector('div.se-wrapper-inner-expanded.se-wrapper-wysiwyg.sun-editor-editable')
            ? element.querySelector('div.se-wrapper-inner-expanded.se-wrapper-wysiwyg.sun-editor-editable')
            : element.querySelector('div.se-wrapper-inner-full-screen.se-wrapper-wysiwyg.sun-editor-editable');
    }

    private getNeedsImagePadding(): boolean {
        const element = this.sunEditorElem.nativeElement.parentElement
            ?.querySelector('div.sun-editor')
            ?.querySelector('div.se-container')
            ?.querySelector('div.se-wrapper');

        const imageElements = element.querySelectorAll('div.se-image-container');
        if (imageElements.length > 0) {
            return element.getBoundingClientRect().bottom - imageElements[imageElements.length - 1].getBoundingClientRect().bottom < 150;
        }

        return false;
    }

    public getSunEditor(): SunEditor | undefined {
        return this.sunEditor;
    }

    private onFullScreen(event: boolean): void {
        const element = this.getInnerWrapper();
        if (event) {
            element.classList.replace('se-wrapper-inner', 'se-wrapper-inner-full-screen');
            element.classList.replace('se-wrapper-inner-expanded', 'se-wrapper-inner-full-screen');
            this.sunEditorElem.nativeElement.nextElementSibling.classList.add('full-screen-width');
            if (this.expandAreaTriggerComponent) {
                this.expandAreaTriggerComponent.triggerReadLess();
            }
        } else {
            element.classList.replace('se-wrapper-inner-full-screen', 'se-wrapper-inner');
            this.sunEditorElem.nativeElement.nextElementSibling.classList.remove('full-screen-width');
        }
    }
}
